import { Injectable, inject } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { map, Observable, tap } from 'rxjs';
import { AdminCosmeticCollectionItem } from '../models/admin-cosmetic-collection-item';
import { AdminCosmeticCollectionFilter } from '../models/admin-cosmetic-collection-filter';
import {
  executeOrNull,
  Guid,
  isEmptyArray,
  once,
  valueOrDefault,
} from '@web/utils';
import {
  AdminCosmeticAttributes,
  AdminCosmeticCharacteristics,
  AdminCosmeticDetailsForm,
} from '../models/admin-cosmetic-details-form';
import {
  AdminMultimediaImageUpload,
  multimediaFromResponse,
} from '@web/shared/module/multimedia';
import { AdminCosmeticDetails } from '../models/admin-cosmetic-details';
import { AdminCosmeticCharacteristicDetails } from '../models/admin-cosmetic-characteristic-details';
import { AdminCosmeticAttributeDetails } from '../models/admin-cosmetic-attribute-details';
import { AdminDictionaryProductAttributeRequest } from '@web/shared/module/dictionary';
import { AdminCosmeticApiService } from '../api/services';
import {
  AdminCosmeticCharacteristicRequest,
  AdminCosmeticCreateRequest,
  AdminCosmeticFileRequest,
  AdminCosmeticUpdateRequest,
} from '../api/requests';

@Injectable({
  providedIn: 'root',
})
export class AdminCosmeticDataSourceService {
  private readonly adminCosmeticApi = inject(AdminCosmeticApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminCosmeticCollectionFilter>,
  ): Observable<TableDataPayload<AdminCosmeticCollectionItem>> {
    /* prettier-ignore */
    return this.adminCosmeticApi
      .fetchCollection({
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.equalFor('status').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('name').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('slug').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('cosmeticBrandName').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('createdAt').build())
              .buildAsArray(),
          )
          .build()
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new AdminCosmeticCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    canBeViewed: item.contextOperations.canBeViewed,
                    cosmeticBrandId: item.cosmeticBrand?.id,
                    cosmeticBrandName: item.cosmeticBrand?.name,
                    createdAt: item.createdAt,
                    featuredFileAlt: item.featuredFile?.alt(),
                    featuredFileUrl: item.featuredFile?.url,
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    slug: item.slug,
                    status: item.status,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public removeById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybrany kosmetyk?')
          .accept(() => {
            this.adminCosmeticApi
              .removeById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Wybrany kosmetyk został usunięty pomyślnie.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public createByFormData(data: AdminCosmeticDetailsForm): Observable<void> {
    return this.adminCosmeticApi.create(
      new AdminCosmeticCreateRequest({
        name: data.name,
        price: valueOrDefault(
          executeOrNull(data.price, (price) => String(price)),
          '',
        ),
        cosmeticBrandId: data.cosmeticBrandId,
        featuredFileId: data.featuredFile?.id,
        vendorUrl: data.vendorUrl,
        status: data.status,
        files: valueOrDefault(
          executeOrNull(data.files, (files) =>
            files.map(({ id }) => new AdminCosmeticFileRequest({ id })),
          ),
          [],
        ),
        attributes: data.attributes?.items.map(
          (attribute) =>
            new AdminDictionaryProductAttributeRequest({
              productAttributeId: attribute.productAttributeId!,
              value: attribute.content,
            }),
        ),
        characteristics: data.characteristics?.items.map(
          (characteristic) =>
            new AdminCosmeticCharacteristicRequest({
              label: characteristic.label,
              value: characteristic.content,
            }),
        ),
        warehouseGoodId: data.warehouseProductId,
      }),
    );
  }

  /* prettier-ignore */
  public fetchDetailsFormById(
    id: string,
  ): Observable<AdminCosmeticDetailsForm> {
    return this.adminCosmeticApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminCosmeticDetailsForm({
            canBeWarehouseProductIdEdited: response.contextOperations.canBeWarehouseGoodIdEdited,
            name: response.name,
            price: response.price,
            attributes: executeOrNull(
              response.attributes,
              (attributes) => new AdminCosmeticAttributes({
                items: attributes.map(({ value, id, productAttribute }) => ({
                  productAttributeId: productAttribute.id,
                  id,
                  content: value,
                })),
              }),
            ),
            characteristics: executeOrNull(
              response.characteristics,
              (characteristics) => new AdminCosmeticCharacteristics({
                items: characteristics.map(({ label, id, value }) => ({
                  label,
                  id,
                  content: value,
                })),
              }),
            ),
            vendorUrl: response.vendorUrl,
            slug: response.slug,
            status: response.status,
            cosmeticBrandId: response.cosmeticBrand?.id,
            featuredFile: executeOrNull(response.featuredFile?.id, (id) => new AdminMultimediaImageUpload({ id })),
            warehouseProductId: response.warehouseGood?.id,
            files: executeOrNull(response.files, (files) => {
              if (isEmptyArray(files)) {
                return null;
              }

              return files.map(({ id }) => new AdminMultimediaImageUpload({ id }));
            }),
            id: response.id,
          }),
      ),
    );
  }

  public updateByFormData(
    id: Guid,
    data: AdminCosmeticDetailsForm,
  ): Observable<void> {
    return this.adminCosmeticApi.update(
      id,
      new AdminCosmeticUpdateRequest({
        name: data.name,
        cosmeticBrandId: data.cosmeticBrandId,
        featuredFileId: data.featuredFile?.id,
        price: valueOrDefault(
          executeOrNull(data.price, (price) => String(price)),
          '',
        ),
        vendorUrl: data.vendorUrl,
        status: data.status,
        files: valueOrDefault(
          executeOrNull(data.files, (files) =>
            files.map(({ id }) => new AdminCosmeticFileRequest({ id })),
          ),
          [],
        ),
        attributes: data.attributes?.items.map(
          (attribute) =>
            new AdminDictionaryProductAttributeRequest({
              id: attribute.id,
              productAttributeId: attribute.productAttributeId!,
              value: attribute.content,
            }),
        ),
        characteristics: data.characteristics?.items.map(
          (characteristic) =>
            new AdminCosmeticCharacteristicRequest({
              id: characteristic.id,
              label: characteristic.label,
              value: characteristic.content,
            }),
        ),
        warehouseGoodId: data.warehouseProductId,
        slug: data.slug,
      }),
    );
  }

  public fetchDetailsById(id: Guid): Observable<AdminCosmeticDetails> {
    return this.adminCosmeticApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminCosmeticDetails({
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
            canBeViewed: response.contextOperations.canBeViewed,
            cosmeticBrandId: response.cosmeticBrand?.id,
            cosmeticBrandName: response.cosmeticBrand?.name,
            createdAt: response.createdAt,
            characteristics: valueOrDefault(
              executeOrNull(response.characteristics, (characteristics) =>
                characteristics.map(
                  (characteristic) =>
                    new AdminCosmeticCharacteristicDetails({
                      id: characteristic.id,
                      content: characteristic.value,
                      label: characteristic.label,
                    }),
                ),
              ),
              [],
            ),
            attributes: valueOrDefault(
              executeOrNull(response.attributes, (attributes) =>
                attributes.map(
                  (attribute) =>
                    new AdminCosmeticAttributeDetails({
                      id: attribute.id,
                      content: attribute.value,
                      label: attribute.productAttribute.label,
                    }),
                ),
              ),
              [],
            ),
            featuredFile: executeOrNull(response.featuredFile, (file) =>
              multimediaFromResponse(file),
            ),
            id: response.id,
            name: response.name,
            price: response.price,
            slug: response.slug,
            status: response.status,
            files: valueOrDefault(
              executeOrNull(response.files, (files) =>
                files.map((file) => multimediaFromResponse(file)),
              ),
              [],
            ),
            vendorUrl: response.vendorUrl,
            warehouseProductName: response.warehouseGood?.name,
          }),
      ),
    );
  }
}
